const PRESS_MODE = Object.freeze({
  NONE: 0,
  FORMING: 1,
  BONDING: 2,
  TUNING: 3,
  TOOL_SWAP: 4,
});

const BONDING_ORIENTATION = Object.freeze({
  CAP_SIDE_DOWN: 0,
  CAP_SIDE_UP: 1,
});

const VACUUM_ZONE = Object.freeze({
  OFF: 0,
  UPPER: 1,
  LOWER: 2,
});

const TUNING_MODE = Object.freeze({
  IDLE: 0,
  TUNING: 1,
  HEAT: 2,
  COOLING: 3,
});

const BONDING_ORIENTATION_LABELS = {
  [BONDING_ORIENTATION.CAP_SIDE_DOWN]: 'Cap Side Down',
  [BONDING_ORIENTATION.CAP_SIDE_UP]: 'Cap Side Up',
};

const VACUUM_ZONE_LABELS = {
  [VACUUM_ZONE.OFF]: 'Off',
  [VACUUM_ZONE.UPPER]: 'Upper',
  [VACUUM_ZONE.LOWER]: 'Lower',
};

const TUNING_MODE_LABELS = {
  [TUNING_MODE.IDLE]: 'Idle',
  [TUNING_MODE.TUNING]: 'Tuning',
  [TUNING_MODE.HEAT]: 'Heat',
  [TUNING_MODE.COOLING]: 'Cooling',
};

const HEAT_ZONE_STAGE_LABELS = {
  0: 'Hold',
  1: 'Pulldown',
};

const CONTROLLER_STATE_LABELS = {
  0: 'Idle',
  1: 'Passive',
  2: 'Active',
  3: 'Reset',
  4: 'Manual',
  5: 'Tuning',
  6: 'Tuned',
  7: 'Self Test',
  8: 'Error',
  9: 'Sync Movement',
};

const HEAT_ZONE_FAULT_REASON = Object.freeze({
  NONE: 0,
  TOOL_RTD_UNDER_TEMP: 1,
  TOOL_RTD_OVER_TEMP: 2,
  TOOL_RTD_SPREAD_TOO_HIGH: 3,
  HEATER_SAFETY_RTD_OVER_TEMP: 4,
  HEAT_RESPONSE_TOO_LOW: 5,
});

const HEAT_ZONE_FAULT_REASON_LABELS = {
  [HEAT_ZONE_FAULT_REASON.NONE]: 'None',
  [HEAT_ZONE_FAULT_REASON.TOOL_RTD_UNDER_TEMP]: 'Tool RTD is below the valid range',
  [HEAT_ZONE_FAULT_REASON.TOOL_RTD_OVER_TEMP]: 'Tool RTD is over the safety limit',
  [HEAT_ZONE_FAULT_REASON.TOOL_RTD_SPREAD_TOO_HIGH]: 'Tool RTD spread is too high',
  [HEAT_ZONE_FAULT_REASON.HEATER_SAFETY_RTD_OVER_TEMP]: 'Heater safety RTD is over the safety limit',
  [HEAT_ZONE_FAULT_REASON.HEAT_RESPONSE_TOO_LOW]: 'Heating rate is too low at full output',
};

const FORMING_CYCLE_STATE_LABELS = {
  0: 'Waiting for Instron to start test',
  10: 'Instron at Position',
  20: 'PLC Ready',
  25: 'Waiting to Heat',
  30: 'Heating to Forming Temperature',
  35: 'Waiting to Presoak',
  40: 'Forming Presoak',
  45: 'Forming Presoak with Vacuum (10 s)',
  50: 'Forming',
  60: 'Cooling to Eject Temperature',
  70: 'Eject Soak',
  80: 'Instron Moving Home',
  100: 'Complete',
};

const BONDING_CYCLE_STATE_LABELS = {
  0: 'Waiting for Instron to start test',
  5: 'Acknowledge Instron',
  10: 'Waiting for Ready Height',
  20: 'Heating to Bonding Temperature',
  25: 'Waiting for Presoak Height',
  30: 'Presoak',
  35: 'Waiting for Bond Force',
  40: 'Bond Hold',
  45: 'Waiting for Superheat Force',
  50: 'Heating to Superheat Temperature',
  55: 'Superheat Hold',
  56: 'Waiting for Anneal Force',
  60: 'Waiting for Annealing Temperature',
  65: 'Annealing Hold',
  70: 'Waiting for Final Temperature',
  75: 'Final Temperature Hold',
  80: 'Instron Moving Home',
  100: 'Complete',
};

const createPath = (name, type, options = {}) => Object.freeze({
  name,
  type,
  ...options,
});

const getPathName = (pathOrString) => (
  typeof pathOrString === 'string' ? pathOrString : pathOrString.name
);

const mapFields = (fieldMap, prefix) => Object.fromEntries(
  Object.entries(fieldMap).map(([key, value]) => {
    if (value?.name && value?.type) {
      return [
        key,
        createPath(`${prefix}.${value.name}`, value.type, value.size ? { size: value.size } : {}),
      ];
    }

    return [key, mapFields(value, prefix)];
  }),
);

const flattenPaths = (node) => {
  if (node?.name && node?.type) {
    return [node];
  }

  return Object.values(node).flatMap(flattenPaths);
};

const dedupePaths = (paths) => {
  const uniqueByName = new Map();
  paths.forEach((path) => {
    uniqueByName.set(path.name, path);
  });
  return [...uniqueByName.values()];
};

const HEAT_ZONE_PID = {
  kp: createPath('fKp', 'LREAL'),
  tnSeconds: createPath('fTnSeconds', 'LREAL'),
  tvSeconds: createPath('fTvSeconds', 'LREAL'),
  tdSeconds: createPath('fTdSeconds', 'LREAL'),
};

const HEAT_ZONE_CONFIG = {
  setpointMin: createPath('fSetpointMin', 'LREAL'),
  setpointMax: createPath('fSetpointMax', 'LREAL'),
  setpointTolerance: createPath('fSetpointTolerance', 'LREAL'),
  pullDownEntryDelta: createPath('fPullDownEntryDelta', 'LREAL'),
  holdEntryDelta: createPath('fHoldEntryDelta', 'LREAL'),
  stableRateThresholdCpm: createPath('fStableRateThresholdCpm', 'LREAL'),
  stableHoldTime: createPath('tStableHoldTime', 'TIME'),
  stableOvershootMax: createPath('fStableOvershootMax', 'LREAL'),
  toolTempMinValid: createPath('fToolTempMinValid', 'LREAL'),
  toolTempMax: createPath('fToolTempMax', 'LREAL'),
  toolRtdSpreadMax: createPath('fToolRtdSpreadMax', 'LREAL'),
  heaterTempMax: createPath('fHeaterTempMax', 'LREAL'),
  minTempRateAtFullOutputCpm: createPath('fMinTempRateAtFullOutputCpm', 'LREAL'),
  faultTempRateAtFullOutputCpm: createPath('fFaultTempRateAtFullOutputCpm', 'LREAL'),
};

const HEAT_ZONE_STATUS = {
  autoTuneActive: createPath('bAutoTuneActive', 'BOOL'),
  tuneComplete: createPath('bTuneComplete', 'BOOL'),
  atSetpoint: createPath('bAtSetpoint', 'BOOL'),
  fault: createPath('bFault', 'BOOL'),
  faultLatched: createPath('bFaultLatched', 'BOOL'),
  faultReason: createPath('eFaultReason', 'INT'),
  heatResponseTooLow: createPath('bHeatResponseTooLow', 'BOOL'),
  heatResponseFault: createPath('bHeatResponseFault', 'BOOL'),
  controllerState: createPath('eControllerState', 'INT'),
  measuredTemp: createPath('fMeasuredTemp', 'LREAL'),
  stableForHold: createPath('bStableForHold', 'BOOL'),
  heatStage: createPath('eHeatStage', 'INT'),
  tempRateCpm: createPath('fTempRateCpm', 'LREAL'),
  appliedSetpoint: createPath('fAppliedSetpoint', 'LREAL'),
  requestedCurrent: createPath('fRequestedCurrent', 'LREAL'),
  analogOutput: createPath('iAnalogOutput', 'INT'),
  outputPercent: createPath('fOutputPercent', 'LREAL'),
  maxOverShoot: createPath('fMaxOverShoot', 'LREAL'),
  pid: mapFields(HEAT_ZONE_PID, 'stPid'),
};

const PLC_PATHS = {
  meta: {
    machineId: createPath('GVL_MachineConfig.stMachineIdentity.sMachineId', 'STRING', { size: 20 }),
    pressId: createPath('GVL_MachineConfig.stMachineIdentity.sPressId', 'STRING', { size: 20 }),
    displayName: createPath('GVL_MachineConfig.stMachineIdentity.sDisplayName', 'STRING', { size: 40 }),
    processLogId: createPath('GVL_Hmi.Meta.sProcessLogId', 'STRING', { size: 40 }),
    // Epoch ms of process_logs.start, written by the HMI so the PLC can anchor
    // telemetry to the same clock as the Instron data. LREAL because the ADS
    // layer has no 64-bit integer type; it is exact for epoch ms.
    runStartMs: createPath('GVL_Hmi.Meta.fRunStartMs', 'LREAL'),
    schemaVersion: createPath('GVL_Hmi.Meta.nSchemaVersion', 'DWORD'),
  },
  cmd: {
    pressMode: createPath('GVL_Hmi.Cmd.ePressMode', 'INT'),
    automationEnable: createPath('GVL_Hmi.Cmd.bAutomationEnable', 'BOOL'),
    standbyHeatToLoadingEnable: createPath('GVL_Hmi.Cmd.bStandbyHeatToLoadingEnable', 'BOOL'),
    ionizerManualEnable: createPath('GVL_Hmi.Cmd.bIonizerManualEnable', 'BOOL'),
    upperCylinderManualEnable: createPath('GVL_Hmi.Cmd.bUpperCylinderManualEnable', 'BOOL'),
    lowerCylinderManualEnable: createPath('GVL_Hmi.Cmd.bLowerCylinderManualEnable', 'BOOL'),
    instronDi1ManualPulse: createPath('GVL_Hmi.Cmd.bInstronDi1ManualPulse', 'BOOL'),
    simulateInstronDo1Pulse: createPath('GVL_Hmi.Cmd.bSimulateInstronDo1Pulse', 'BOOL'),
    resetHeaterCheck: createPath('GVL_Hmi.Cmd.bResetHeaterCheck', 'BOOL'),
    vacuumManualEnable: createPath('GVL_Hmi.Cmd.bVacuumManualEnable', 'BOOL'),
    vacuumManualZone: createPath('GVL_Hmi.Cmd.eVacuumManualZone', 'INT'),
    upperHeatPid: mapFields(HEAT_ZONE_PID, 'GVL_MachineConfig.stUpperHeatPid'),
    lowerHeatPid: mapFields(HEAT_ZONE_PID, 'GVL_MachineConfig.stLowerHeatPid'),
    upperHeatZoneConfig: mapFields(HEAT_ZONE_CONFIG, 'GVL_MachineConfig.stUpperHeatZoneConfig'),
    lowerHeatZoneConfig: mapFields(HEAT_ZONE_CONFIG, 'GVL_MachineConfig.stLowerHeatZoneConfig'),
    formingRecipe: {
      upperLoadingSetpoint: createPath('GVL_Hmi.Cmd.stFormingRecipe.fUpperLoadingSetpoint', 'LREAL'),
      lowerLoadingSetpoint: createPath('GVL_Hmi.Cmd.stFormingRecipe.fLowerLoadingSetpoint', 'LREAL'),
      upperFormingSetpoint: createPath('GVL_Hmi.Cmd.stFormingRecipe.fUpperFormingSetpoint', 'LREAL'),
      lowerFormingSetpoint: createPath('GVL_Hmi.Cmd.stFormingRecipe.fLowerFormingSetpoint', 'LREAL'),
      upperEjectSetpoint: createPath('GVL_Hmi.Cmd.stFormingRecipe.fUpperEjectSetpoint', 'LREAL'),
      lowerEjectSetpoint: createPath('GVL_Hmi.Cmd.stFormingRecipe.fLowerEjectSetpoint', 'LREAL'),
      formingPresoakTime: createPath('GVL_Hmi.Cmd.stFormingRecipe.tFormingPresoakTime', 'TIME'),
      ejectSoakTime: createPath('GVL_Hmi.Cmd.stFormingRecipe.tEjectSoakTime', 'TIME'),
      autoVacuum: createPath('GVL_Hmi.Cmd.stFormingRecipe.bAutoVacuum', 'BOOL'),
      autoIonizer: createPath('GVL_Hmi.Cmd.stFormingRecipe.bAutoIonizer', 'BOOL'),
    },
    bondingRecipe: {
      autoIonizer: createPath('GVL_Hmi.Cmd.stBondingRecipe.bAutoIonizer', 'BOOL'),
      bondingVacuumEnable: createPath('GVL_Hmi.Cmd.stBondingRecipe.bBondingVacuumEnable', 'BOOL'),
      presoakTime: createPath('GVL_Hmi.Cmd.stBondingRecipe.tPresoakTime', 'TIME'),
      bondTime: createPath('GVL_Hmi.Cmd.stBondingRecipe.tBondTime', 'TIME'),
      superheatWaitTime: createPath('GVL_Hmi.Cmd.stBondingRecipe.tSuperheatWaitTime', 'TIME'),
      annealingWaitTime: createPath('GVL_Hmi.Cmd.stBondingRecipe.tAnnealingWaitTime', 'TIME'),
      finalTempWaitTime: createPath('GVL_Hmi.Cmd.stBondingRecipe.tFinalTempWaitTime', 'TIME'),
      upperLoadingSetpoint: createPath('GVL_Hmi.Cmd.stBondingRecipe.fUpperLoadingSetpoint', 'LREAL'),
      lowerLoadingSetpoint: createPath('GVL_Hmi.Cmd.stBondingRecipe.fLowerLoadingSetpoint', 'LREAL'),
      upperBondingSetpoint: createPath('GVL_Hmi.Cmd.stBondingRecipe.fUpperBondingSetpoint', 'LREAL'),
      upperBondingTolerance: createPath('GVL_Hmi.Cmd.stBondingRecipe.fUpperBondingTolerance', 'LREAL'),
      lowerBondingSetpoint: createPath('GVL_Hmi.Cmd.stBondingRecipe.fLowerBondingSetpoint', 'LREAL'),
      lowerBondingTolerance: createPath('GVL_Hmi.Cmd.stBondingRecipe.fLowerBondingTolerance', 'LREAL'),
      upperSuperheatSetpoint: createPath('GVL_Hmi.Cmd.stBondingRecipe.fUpperSuperheatSetpoint', 'LREAL'),
      upperSuperheatTolerance: createPath('GVL_Hmi.Cmd.stBondingRecipe.fUpperSuperheatTolerance', 'LREAL'),
      lowerSuperheatSetpoint: createPath('GVL_Hmi.Cmd.stBondingRecipe.fLowerSuperheatSetpoint', 'LREAL'),
      lowerSuperheatTolerance: createPath('GVL_Hmi.Cmd.stBondingRecipe.fLowerSuperheatTolerance', 'LREAL'),
      upperAnnealingSetpoint: createPath('GVL_Hmi.Cmd.stBondingRecipe.fUpperAnnealingSetpoint', 'LREAL'),
      upperAnnealingTolerance: createPath('GVL_Hmi.Cmd.stBondingRecipe.fUpperAnnealingTolerance', 'LREAL'),
      lowerAnnealingSetpoint: createPath('GVL_Hmi.Cmd.stBondingRecipe.fLowerAnnealingSetpoint', 'LREAL'),
      lowerAnnealingTolerance: createPath('GVL_Hmi.Cmd.stBondingRecipe.fLowerAnnealingTolerance', 'LREAL'),
      upperFinalSetpoint: createPath('GVL_Hmi.Cmd.stBondingRecipe.fUpperFinalSetpoint', 'LREAL'),
      lowerFinalSetpoint: createPath('GVL_Hmi.Cmd.stBondingRecipe.fLowerFinalSetpoint', 'LREAL'),
    },
    tuningRecipe: {
      mode: createPath('GVL_Hmi.Cmd.stTuningRecipe.eMode', 'INT'),
      upperZone: {
        heatSetpoint: createPath('GVL_Hmi.Cmd.stTuningRecipe.stUpperZone.fHeatSetpoint', 'LREAL'),
        coolSetpoint: createPath('GVL_Hmi.Cmd.stTuningRecipe.stUpperZone.fCoolSetpoint', 'LREAL'),
        seedPid: mapFields(HEAT_ZONE_PID, 'GVL_Hmi.Cmd.stTuningRecipe.stUpperZone.stSeedPid'),
      },
      lowerZone: {
        heatSetpoint: createPath('GVL_Hmi.Cmd.stTuningRecipe.stLowerZone.fHeatSetpoint', 'LREAL'),
        coolSetpoint: createPath('GVL_Hmi.Cmd.stTuningRecipe.stLowerZone.fCoolSetpoint', 'LREAL'),
        seedPid: mapFields(HEAT_ZONE_PID, 'GVL_Hmi.Cmd.stTuningRecipe.stLowerZone.stSeedPid'),
      },
    },
  },
  status: {
    pressInputs: {
      instronDo1: createPath('GVL_Hmi.Status.stPressInputs.InstronDo1', 'BOOL'),
      airFeedback: createPath('GVL_Hmi.Status.stPressInputs.AirFdbk', 'BOOL'),
      chillerFeedback: createPath('GVL_Hmi.Status.stPressInputs.ChillerFdbk', 'BOOL'),
      heaterEmergencySwitch: createPath('GVL_Hmi.Status.stPressInputs.HeaterEmergencySwitch', 'BOOL'),
      upperCylinderEngageFeedback: createPath('GVL_Hmi.Status.stPressInputs.UpperCylinderEngageFdbk', 'BOOL'),
      upperCylinderRetractFeedback: createPath('GVL_Hmi.Status.stPressInputs.UpperCylinderRetractFdbk', 'BOOL'),
      lowerCylinderEngageFeedback: createPath('GVL_Hmi.Status.stPressInputs.LowerCylinderEngageFdbk', 'BOOL'),
      lowerCylinderRetractFeedback: createPath('GVL_Hmi.Status.stPressInputs.LowerCylinderRetractFdbk', 'BOOL'),
      upperToolRtd1: createPath('GVL_Hmi.Status.stPressInputs.UpperToolRtd1', 'INT'),
      upperToolRtd2: createPath('GVL_Hmi.Status.stPressInputs.UpperToolRtd2', 'INT'),
      upperToolRtd3: createPath('GVL_Hmi.Status.stPressInputs.UpperToolRtd3', 'INT'),
      upperPeekRtd: createPath('GVL_Hmi.Status.stPressInputs.UpperPeekRtd', 'INT'),
      lowerToolRtd1: createPath('GVL_Hmi.Status.stPressInputs.LowerToolRtd1', 'INT'),
      lowerToolRtd2: createPath('GVL_Hmi.Status.stPressInputs.LowerToolRtd2', 'INT'),
      lowerToolRtd3: createPath('GVL_Hmi.Status.stPressInputs.LowerToolRtd3', 'INT'),
      lowerHeaterRtd: createPath('GVL_Hmi.Status.stPressInputs.LowerHeaterRtd', 'INT'),
      vacuum4to20mAFeedback: createPath('GVL_Hmi.Status.stPressInputs.Vacuum4to20mAFdbk', 'INT'),
    },
    safetyOk: createPath('GVL_Hmi.Status.bSafetyOk', 'BOOL'),
    airChillerOn: createPath('GVL_Hmi.Status.bAirChillerOn', 'BOOL'),
    vacuumPowerOn: createPath('GVL_Hmi.Status.bVacuumPowerOn', 'BOOL'),
    vacuumZoneOpen: createPath('GVL_Hmi.Status.eVacuumZoneOpen', 'INT'),
    ionizerOn: createPath('GVL_Hmi.Status.bIonizerOn', 'BOOL'),
    cycleRunning: createPath('GVL_Hmi.Status.bCycleRunning', 'BOOL'),
    canForcePLCForward: createPath('GVL_Hmi.Status.bCanForcePLCForward', 'BOOL'),
    canForceInstronForward: createPath('GVL_Hmi.Status.bCanForceInstronForward', 'BOOL'),
    cycleElapsed: createPath('GVL_Hmi.Status.tCycleElapsed', 'TIME'),
    stateElapsed: createPath('GVL_Hmi.Status.tStateElapsed', 'TIME'),
    stateRemaining: createPath('GVL_Hmi.Status.tStateRemaining', 'TIME'),
    heatAutoShutdownRemaining: createPath('GVL_Hmi.Status.tHeatAutoShutdownRemaining', 'TIME'),
    upperToolTempAvg: createPath('GVL_Hmi.Status.fUpperToolTempAvg', 'LREAL'),
    lowerToolTempAvg: createPath('GVL_Hmi.Status.fLowerToolTempAvg', 'LREAL'),
    upperAppliedSetpoint: createPath('GVL_Hmi.Status.fUpperAppliedSetpoint', 'LREAL'),
    lowerAppliedSetpoint: createPath('GVL_Hmi.Status.fLowerAppliedSetpoint', 'LREAL'),
    safeToSwapTool: createPath('GVL_Hmi.Status.bSafeToSwapTool', 'BOOL'),
    toolSwapStabilizeRemaining: createPath('GVL_Hmi.Status.tToolSwapStabilizeRemaining', 'TIME'),
    formingCycleState: createPath('GVL_Hmi.Status.eFormingCycleState', 'INT'),
    bondingCycleState: createPath('GVL_Hmi.Status.eBondingCycleState', 'INT'),
    upperHeatBlock: mapFields(HEAT_ZONE_STATUS, 'GVL_Hmi.Status.stUpperHeatBlock'),
    lowerHeatBlock: mapFields(HEAT_ZONE_STATUS, 'GVL_Hmi.Status.stLowerHeatBlock'),
  },
};

const PRESS_HMI_GROUPS = {
  base: dedupePaths([
    ...flattenPaths(PLC_PATHS.meta),
    PLC_PATHS.cmd.pressMode,
  ]),
  common: dedupePaths([
    PLC_PATHS.cmd.automationEnable,
    PLC_PATHS.cmd.standbyHeatToLoadingEnable,
    PLC_PATHS.cmd.ionizerManualEnable,
    PLC_PATHS.cmd.vacuumManualEnable,
    PLC_PATHS.cmd.vacuumManualZone,
    PLC_PATHS.cmd.upperCylinderManualEnable,
    PLC_PATHS.cmd.lowerCylinderManualEnable,
    PLC_PATHS.cmd.instronDi1ManualPulse,
    PLC_PATHS.cmd.simulateInstronDo1Pulse,
    PLC_PATHS.cmd.resetHeaterCheck,
    ...flattenPaths(PLC_PATHS.cmd.upperHeatPid),
    ...flattenPaths(PLC_PATHS.cmd.lowerHeatPid),
    ...flattenPaths(PLC_PATHS.cmd.upperHeatZoneConfig),
    ...flattenPaths(PLC_PATHS.cmd.lowerHeatZoneConfig),
    PLC_PATHS.status.safetyOk,
    PLC_PATHS.status.airChillerOn,
    PLC_PATHS.status.vacuumPowerOn,
    PLC_PATHS.status.vacuumZoneOpen,
    PLC_PATHS.status.ionizerOn,
    PLC_PATHS.status.cycleRunning,
    PLC_PATHS.status.canForcePLCForward,
    PLC_PATHS.status.canForceInstronForward,
    PLC_PATHS.status.cycleElapsed,
    PLC_PATHS.status.stateElapsed,
    PLC_PATHS.status.stateRemaining,
    PLC_PATHS.status.heatAutoShutdownRemaining,
    PLC_PATHS.status.upperToolTempAvg,
    PLC_PATHS.status.lowerToolTempAvg,
    PLC_PATHS.status.upperAppliedSetpoint,
    PLC_PATHS.status.lowerAppliedSetpoint,
    ...flattenPaths(PLC_PATHS.status.pressInputs),
    ...flattenPaths(PLC_PATHS.status.upperHeatBlock),
    ...flattenPaths(PLC_PATHS.status.lowerHeatBlock),
  ]),
  forming: dedupePaths([
    PLC_PATHS.status.formingCycleState,
    ...flattenPaths(PLC_PATHS.cmd.formingRecipe),
  ]),
  bonding: dedupePaths([
    PLC_PATHS.status.bondingCycleState,
    ...flattenPaths(PLC_PATHS.cmd.bondingRecipe),
  ]),
  tuning: dedupePaths([
    ...flattenPaths(PLC_PATHS.cmd.tuningRecipe),
  ]),
  toolSwap: dedupePaths([
    PLC_PATHS.status.safeToSwapTool,
    PLC_PATHS.status.toolSwapStabilizeRemaining,
  ]),
};

const getGroupHandles = (groupNames) => dedupePaths(
  groupNames.flatMap((groupName) => PRESS_HMI_GROUPS[groupName] || []),
);

export {
  BONDING_ORIENTATION,
  BONDING_ORIENTATION_LABELS,
  BONDING_CYCLE_STATE_LABELS,
  CONTROLLER_STATE_LABELS,
  createPath,
  dedupePaths,
  flattenPaths,
  getGroupHandles,
  getPathName,
  PLC_PATHS,
  PRESS_HMI_GROUPS,
  PRESS_MODE,
  TUNING_MODE,
  TUNING_MODE_LABELS,
  VACUUM_ZONE,
  VACUUM_ZONE_LABELS,
  HEAT_ZONE_STAGE_LABELS,
  HEAT_ZONE_FAULT_REASON,
  HEAT_ZONE_FAULT_REASON_LABELS,
  FORMING_CYCLE_STATE_LABELS,
};
